import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Globe,
  Navigation,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { playwrightGoto } from 'sprites-types/workflow/tools/playwrightGoto'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function CollapsibleHtmlSection({ html }: { html: string }) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 text-xs font-medium text-gray-700 transition-colors hover:text-gray-900"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        )}
        <span>HTML Source {isExpanded ? '(hide)' : '(show)'}</span>
        <span className="text-[10px] text-gray-500">({(html.length / 1024).toFixed(1)} KB)</span>
      </button>
      {isExpanded && (
        <div className="mt-2 rounded-2xl bg-gray-50 p-3">
          <pre className="max-h-96 overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words text-[10px] text-gray-800">
            {html}
          </pre>
        </div>
      )}
    </div>
  )
}

function PlaywrightGotoDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(playwrightGoto, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const url = inputs.url || inputs.website || inputs.page || ''
  const responses = Array.isArray(result) ? result : [result]
  const response = responses[0] // Display first response, but preserve all data
  const finalUrl = url // fallback to input URL since result doesn't have URL
  const success = response.success !== false && !response.error
  const error = response.error || ''
  const title = response.metadata?.ogTitle || response.metadata?.description || ''
  const loadTime = 0 // not available in result type
  const statusCode = response.status || 0

  const getDomain = (urlStr: string) => {
    try {
      return new URL(urlStr).hostname
    } catch {
      return urlStr
    }
  }

  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'text-green-600'
    if (code >= 300 && code < 400) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-purple-100 p-2">
              {success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <Navigation className="h-4 w-4 text-purple-600" />
              <div className="text-sm font-semibold text-gray-900">
                {success ? 'Page Navigation Complete' : 'Page Navigation Failed'}
              </div>
              {success && <CheckCircle className="h-4 w-4 text-green-600" />}
            </div>

            <div className="mb-3 break-all text-xs text-gray-600">
              <span className="font-medium">{finalUrl || url}</span>
            </div>

            {error && !success && (
              <div className="mb-3 rounded-2xl bg-red-50 p-2 text-xs text-red-600">{error}</div>
            )}

            <div className="mb-3 flex items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                <span>{getDomain(finalUrl || url)}</span>
              </div>
              {statusCode > 0 && (
                <div className={`font-medium ${getStatusColor(statusCode)}`}>{statusCode}</div>
              )}
              {loadTime > 0 && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{loadTime.toFixed(0)}ms</span>
                </div>
              )}
            </div>

            {title && (
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-gray-700">Page Title:</div>
                <div className="rounded-2xl bg-gray-50 p-2 text-xs text-gray-800">
                  {title.length > 100 ? `${title.slice(0, 100)}...` : title}
                </div>
              </div>
            )}

            {success && response.text && (
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-gray-700">Content:</div>
                <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-gray-50 p-3 text-xs text-gray-800">
                  {response.text.length > 5000
                    ? `${response.text.slice(0, 5000)}...`
                    : response.text}
                </div>
              </div>
            )}

            {success && response.content && <CollapsibleHtmlSection html={response.content} />}

            {success && finalUrl && finalUrl !== url && (
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-gray-700">Original URL:</div>
                <div className="break-all text-xs text-gray-600">{url}</div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-2">
              <a
                href={finalUrl || url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
              >
                <ExternalLink className="h-3 w-3" />
                View Page
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadPlaywrightGoto(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function playwrightGotoToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(playwrightGoto, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const url = inputs.url || inputs.website || inputs.page || ''
  const responses = Array.isArray(result) ? result : [result]
  const response = responses[0] // Display first response, but preserve all data
  const finalUrl = url // fallback to input URL since result doesn't have URL
  const success = response?.success !== false && !response?.error
  const error = response?.error || ''
  const title = response?.metadata?.ogTitle || response?.metadata?.description || ''
  const loadTime = 0 // not available in result type
  const statusCode = response?.status || 0

  return `## Playwright: Page Navigation

**Status:** ${success ? '✅ Success' : '❌ Failed'}
**URL:** ${finalUrl || url}
${title ? `**Page Title:** ${title}` : ''}
${statusCode > 0 ? `**Status Code:** ${statusCode}` : ''}
${loadTime > 0 ? `**Load Time:** ${loadTime}ms` : ''}
${error ? `**Error:** ${error}` : ''}
${success && finalUrl && finalUrl !== url ? `**Original URL:** ${url}` : ''}

---
`
}

export const PlaywrightGotoDisplay = createWorkflowToolComponent(
  PlaywrightGotoDisplayComponent,
  downloadPlaywrightGoto,
  playwrightGotoToMarkdown
)
